import { useState, useCallback } from 'react';
import { notify } from '../../../utils/toast';
import { useCrmData } from '../../../contexts/CrmProviders';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAppContext } from '../../../contexts/AppContext';
import { SubscriptionPlan } from '../../../types';
import {
    hasBillableStripeSubscription,
    isUnlimited,
    type PlanId,
    type SubscriptionPlan as DomainPlan,
} from '../../../domain/entities/Subscription';
import {
    createCheckoutSession,
    fetchOrganizationSubscription,
    updateSubscriptionQuantity,
} from '../../../services/modules/subscriptionService';
import { fetchOrganizationLimits } from '../../../services/modules/subscription/subscription.limits';
import { CHECKOUT_SESSION_KEY } from './useCheckoutBillingSync';

// =============================================================================
// TIPOS
// =============================================================================

export interface SubscriptionActionsState {
    billingCycle: 'monthly' | 'annually';
    isSelectionModalOpen: boolean;
    planToConfigure: SubscriptionPlan | null;
    isCheckoutLoading: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

export const useSubscriptionActions = (
    stripePlans: DomainPlan[],
    legacyPlans: SubscriptionPlan[]
) => {
    const { updateSubscription } = useCrmData();
    const { setCurrentPage } = useAppContext();
    const { currentOrganization, refreshOrganizations } = useOrganization();

    // Estado UI
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'annually'>('monthly');
    const [isSelectionModalOpen, setSelectionModalOpen] = useState(false);
    const [planToConfigure, setPlanToConfigure] = useState<SubscriptionPlan | null>(null);
    const [isCheckoutLoading, setCheckoutLoading] = useState(false);

    // =================================================================
    // HANDLERS
    // =================================================================

    const handleToggle = useCallback(() => {
        setBillingCycle(prev => prev === 'monthly' ? 'annually' : 'monthly');
    }, []);

    const handleSelectPlan = useCallback((plan: SubscriptionPlan) => {
        setPlanToConfigure(plan);
        setSelectionModalOpen(true);
    }, []);

    const closeSelectionModal = useCallback(() => {
        setSelectionModalOpen(false);
        setPlanToConfigure(null);
    }, []);

    /**
     * Inicia checkout do Stripe para o plano selecionado
     */
    const handleConfirmSubscription = useCallback(async (planId: string, userCount: number) => {
        const selectedPlan = stripePlans.find(p => p.id === planId)
            || legacyPlans.find(p => p.id === planId);

        if (!selectedPlan) {
            notify.error('Plano não encontrado');
            closeSelectionModal();
            return;
        }

        const planMinUsers = stripePlans.find(p => p.id === planId)?.minUsers ?? 1;
        if (userCount < planMinUsers) {
            notify.error(`A quantidade de usuários deve ser pelo menos ${planMinUsers}`);
            return;
        }

        let redirectingToStripe = false;

        try {
            setCheckoutLoading(true);

            // ===========================================
            // USAGE GUARDRAILS
            // ===========================================
            if (currentOrganization?.id && stripePlans.some(p => p.id === planId)) {
                // Apenas validar se tivermos acesso aos limites do plano (DomainPlan)
                const targetSubscriptionPlan = stripePlans.find(p => p.id === planId);
                
                if (targetSubscriptionPlan) {
                    try {
                        const usage = await fetchOrganizationLimits(currentOrganization.id);
                        
                        // Validar Funis
                        if (!isUnlimited(targetSubscriptionPlan.maxFunnels) && usage.funnels.current > targetSubscriptionPlan.maxFunnels) {
                            notify.error(`Uso atual de funis (${usage.funnels.current}) excede o limite do plano (${targetSubscriptionPlan.maxFunnels}). Remova funis antes de mudar.`);
                            return;
                        }

                        // Validar Contatos
                        if (!isUnlimited(targetSubscriptionPlan.maxContacts) && usage.contacts.current > targetSubscriptionPlan.maxContacts) {
                            notify.error(`Uso atual de contatos (${usage.contacts.current}) excede o limite do plano (${targetSubscriptionPlan.maxContacts}). Remova contatos antes de mudar.`);
                            return;
                        }

                        // Validar WhatsApp
                        if (!isUnlimited(targetSubscriptionPlan.maxWhatsapp) && usage.whatsapp.current > targetSubscriptionPlan.maxWhatsapp) {
                            notify.error(`Uso atual de conexões WhatsApp (${usage.whatsapp.current}) excede o limite do plano (${targetSubscriptionPlan.maxWhatsapp}). Remova conexões antes de mudar.`);
                            return;
                        }
                    } catch (err) {
                        console.error('[useSubscriptionActions] Failed to validate limits:', err);
                        // Opcional: Bloquear se não conseguir validar, ou permitir com aviso. Permitindo por enquanto.
                    }
                }
            }

            if (!currentOrganization?.id) {
                notify.error('Organização não selecionada.');
                return;
            }

            const existing = await fetchOrganizationSubscription(currentOrganization.id);
            // Trial local (trialing sem stripe_subscription_id) → Checkout, não update
            const hasStripeSub = hasBillableStripeSubscription(existing);

            if (hasStripeSub) {
                const result = await updateSubscriptionQuantity(
                    currentOrganization.id,
                    userCount,
                    planId as PlanId
                );
                await refreshOrganizations();
                window.dispatchEvent(new CustomEvent('crm:billing-updated'));
                notify.success(result.message || 'Plano atualizado com sucesso.');
                closeSelectionModal();
                return;
            }

            const checkout = await createCheckoutSession({
                organizationId: currentOrganization.id,
                planId: planId as PlanId,
                quantity: userCount,
                successUrl: `${window.location.origin}/subscription?success=true&session_id={CHECKOUT_SESSION_ID}`,
                cancelUrl: `${window.location.origin}/subscription?canceled=true`,
            });

            if (checkout.url) {
                redirectingToStripe = true;
                if (checkout.sessionId) {
                    sessionStorage.setItem(CHECKOUT_SESSION_KEY, checkout.sessionId);
                }
                window.location.assign(checkout.url);
                return;
            }

            if (checkout.synced || checkout.alreadySubscribed) {
                await refreshOrganizations();
                window.dispatchEvent(new CustomEvent('crm:billing-updated'));
                notify.success('Plano ativado com sucesso!');
                closeSelectionModal();
                setCurrentPage('dashboard');
                window.history.replaceState(null, '', '/dashboard');
                return;
            }

            throw new Error('URL de checkout não retornada');

        } catch (error) {
            console.error('[useSubscriptionActions] Checkout error:', error);
            notify.error(error instanceof Error ? error.message : 'Erro ao iniciar checkout');

            if (import.meta.env.DEV) {
                updateSubscription(planId, userCount, billingCycle);
                notify.success(`[DEV] Plano atualizado localmente para "${selectedPlan.name}"`);
                closeSelectionModal();
            }
        } finally {
            if (!redirectingToStripe) {
                setCheckoutLoading(false);
            }
        }
    }, [stripePlans, legacyPlans, billingCycle, updateSubscription, closeSelectionModal, currentOrganization?.id, refreshOrganizations, setCurrentPage]);

    const navigateToBilling = useCallback(() => {
        setCurrentPage('billing');
    }, [setCurrentPage]);

    // =================================================================
    // RETORNO
    // =================================================================

    return {
        // Estado UI
        billingCycle,
        isSelectionModalOpen,
        planToConfigure,
        isCheckoutLoading,

        // Handlers
        handleToggle,
        handleSelectPlan,
        handleConfirmSubscription,
        closeSelectionModal,
        navigateToBilling,
    };
};
